import { Fragment, memo, useEffect, useState } from "react";

import { updateRoleCategories } from "@/api/category";
import { createRole, editRole } from "@/api/role";
import { getAllWorkflows, getWorkflowStartNodes } from "@/api/workflow";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { HashLoader } from "react-spinners";

import { RoleType } from "@/types/role";

import { getCategoryIds } from "@/utils/category";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import CategoryMultiSelect from "../category-multi-select";

interface Props {
  role: RoleType | null;
  isOpen: boolean;
  onClose: () => void;
}

const RoleModal: React.FC<Props> = ({ isOpen, onClose, role }) => {
  const title = role ? `ویرایش نقش ${role.nameFa}` : "ساخت نقش جدید";
  const description = role ? "ویرایش نقش" : "ساخت نقش جدید برای سیستم";

  const onChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Content role={role} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({ role, onClose }: { role: RoleType | null; onClose: () => void }) => {
    const queryClient = useQueryClient();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
      () => getCategoryIds(role)
    );
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(() =>
      String(role?.workflowId ?? "")
    );
    const [selectedNodeId, setSelectedNodeId] = useState<string>(
      () => role?.nodeId ?? ""
    );

    const { ref: workflowRef, inView: workflowInView } = useInView();

    const {
      isFetchingNextPage: isFetchingNextPageWorkflows,
      hasNextPage: hasNextPageWorkflows,
      data: workflows,
      fetchNextPage: fetchNextPageWorkflows
    } = useInfiniteQuery({
      queryKey: ["workflows"],
      queryFn: async ({ pageParam }) => {
        return await getAllWorkflows({ page: pageParam, size: 10, search: "" });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPage?.data?.length === 0) {
          return undefined;
        }

        return lastPageParam + 1;
      }
    });

    const { data: startNodes, isLoading: isLoadingStartNodes } = useQuery({
      queryKey: ["workflow-start-nodes", selectedWorkflowId],
      queryFn: () => getWorkflowStartNodes(Number(selectedWorkflowId)),
      enabled: selectedWorkflowId !== "" && selectedWorkflowId !== "none"
    });

    useEffect(() => {
      if (workflowInView) {
        fetchNextPageWorkflows();
      }
    }, [fetchNextPageWorkflows, workflowInView]);

    useEffect(() => {
      setSelectedCategoryIds(getCategoryIds(role));
      setSelectedWorkflowId(String(role?.workflowId ?? ""));
      setSelectedNodeId(role?.nodeId ?? "");
    }, [role]);

    const { mutate, isPending } = useMutation({
      mutationFn: async (data: any) => {
        const response = role ? await editRole(data) : await createRole(data);
        const targetId =
          role?.id ??
          (response as { data?: { id?: number } } | undefined)?.data?.id;

        if (targetId && selectedCategoryIds.length) {
          await updateRoleCategories(targetId, selectedCategoryIds);
        }

        return response;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["roles"] });
        onClose();
      }
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const formData = new FormData(e.currentTarget);
      const data: Record<any, any> = Object.fromEntries(formData);
      if (role) {
        data.id = role.id;
      }

      const workflowId = selectedWorkflowId ? Number(selectedWorkflowId) : null;
      data.workflowId = workflowId;
      data.nodeId = workflowId ? selectedNodeId || null : null;

      mutate(data);
    };

    return (
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-1">
          <label htmlFor=""> نام نقش (فارسی)</label>
          <Input
            type="text"
            name="nameFa"
            placeholder="نام نفش"
            defaultValue={role?.nameFa ?? ""}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor=""> نام نقش (انگلیسی)</label>
          <Input
            type="text"
            name="nameEn"
            placeholder="نام نفش"
            defaultValue={role?.nameEn ?? ""}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor=""> نام نمایشی نقش (فارسی)</label>
          <Input
            type="text"
            name="displayNameFa"
            placeholder="نام نمایشی نقش"
            defaultValue={role?.displayNameFa ?? ""}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor=""> نام نمایشی نقش (انگلیسی)</label>
          <Input
            type="text"
            name="displayNameEn"
            placeholder="نام نمایشی نقش"
            defaultValue={role?.displayNameEn ?? ""}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="">توضیحات</label>
          <Input
            type="text"
            name="description"
            placeholder="توضیحات"
            defaultValue={role?.description ?? ""}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="">گردش کار</label>
          <Select
            dir="rtl"
            name="workflowId"
            value={selectedWorkflowId || "none"}
            onValueChange={(value) => {
              setSelectedWorkflowId(value);
              setSelectedNodeId("");
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="انتخاب گردش کار" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">بدون گردش کار</SelectItem>
              {workflows?.pages.map((workflowPage, index) => (
                <Fragment key={index}>
                  {workflowPage?.data.map((workflow) => (
                    <SelectItem key={workflow.id} value={String(workflow.id)}>
                      {workflow.name}
                    </SelectItem>
                  ))}
                </Fragment>
              ))}
              {hasNextPageWorkflows && (
                <div
                  ref={workflowRef}
                  className="flex w-full justify-center py-1 text-center"
                >
                  {isFetchingNextPageWorkflows ? (
                    <HashLoader size={30} color="#0099A5" />
                  ) : (
                    "بیشتر"
                  )}
                </div>
              )}
            </SelectContent>
          </Select>
        </div>

        {selectedWorkflowId && selectedWorkflowId !== "none" && (
          <div className="space-y-1">
            <label htmlFor="">نود شروع</label>
            <Select
              dir="rtl"
              name="nodeId"
              value={selectedNodeId}
              onValueChange={setSelectedNodeId}
            >
              <SelectTrigger>
                <SelectValue placeholder="انتخاب نود شروع" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingStartNodes ? (
                  <div className="flex w-full justify-center py-2">
                    <HashLoader size={30} color="#0099A5" />
                  </div>
                ) : startNodes?.data && startNodes.data.length > 0 ? (
                  startNodes.data.map((node: any) => (
                    <SelectItem key={node.id} value={node.id}>
                      {node.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="py-2 text-center text-sm text-slate-500">
                    هیچ نود شروعی یافت نشد
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        <CategoryMultiSelect
          value={selectedCategoryIds}
          onChange={setSelectedCategoryIds}
          title="دسته بندی های نقش"
          description="یک یا چند دسته بندی برای نقش انتخاب کنید"
        />

        <Button type="submit" className="w-full" disabled={isPending}>
          ذخیره
        </Button>
      </form>
    );
  }
);

export default RoleModal;
